/* Size memory - remember the shopper's size across PDPs.
 *
 * Write : any change inside <variant-selects> saves {size, dimension, height}.
 * Read  : on the next PDP, resolve the best AVAILABLE variant for the saved size
 *         and select it. Exact L x B x H first, then progressively looser matches,
 *         then the first available variant ("first active size").
 *
 * Storage: localStorage (primary) mirrored into a 30-day cookie, so the preference
 * survives the session and still works where localStorage is blocked.
 *
 * The picker is driven the same way a shopper drives it - set the radios, fire one
 * change event, let product-info.js re-render. Nothing else in the PDP is touched.
 */
(function () {
  'use strict';

  var KEY = 'dfx_size_pref';
  var COOKIE_DAYS = 30;
  var DATA_EL = 'dfx-size-memory-data';

  /* -- storage -- */
  function readCookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : '';
  }
  function writeCookie(name, value, days) {
    try {
      document.cookie = name + '=' + encodeURIComponent(value) +
        '; path=/; max-age=' + (days * 86400) + '; samesite=lax';
    } catch (e) {}
  }
  function load() {
    var raw = '';
    try { raw = window.localStorage.getItem(KEY) || ''; } catch (e) {}
    if (!raw) raw = readCookie(KEY);
    if (!raw) return null;
    try {
      var p = JSON.parse(raw);
      return p && typeof p === 'object' ? p : null;
    } catch (e) { return null; }
  }
  function save(pref) {
    var raw = JSON.stringify(pref);
    try { window.localStorage.setItem(KEY, raw); } catch (e) {}
    writeCookie(KEY, raw, COOKIE_DAYS);
  }

  /* -- normalisation --
   * Option values are inconsistent across products: "Dimension" vs "Dimensions",
   * 6 (15.2cm) vs 6" (15.2 cm), 72 x 36 vs 72x36. Compare on a canonical form.
   */
  function norm(v) {
    return String(v == null ? '' : v)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '');
  }
  function slotFor(optionName) {
    var n = String(optionName || '').toLowerCase();
    if (n.indexOf('dimension') > -1) return 'dimension';
    if (n.indexOf('height') > -1) return 'height';
    if (n.indexOf('size') > -1) return 'size';
    return null;
  }

  /* -- product data -- */
  var dataEl = document.getElementById(DATA_EL);
  if (!dataEl) return;
  var data;
  try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }
  if (!data || !data.variants || !data.variants.length) return;

  var slotPos = {};
  (data.options || []).forEach(function (name, i) {
    var s = slotFor(name);
    if (s && slotPos[s] === undefined) slotPos[s] = i;
  });

  function variantSlot(v, slot) {
    var i = slotPos[slot];
    return i === undefined ? null : v.o[i];
  }

  var applying = false;

  /* -- save the current selection -- */
  function currentPrefFromDom() {
    var vs = document.querySelector('variant-selects');
    if (!vs) return null;
    var pref = {};
    vs.querySelectorAll('fieldset.product-form__input').forEach(function (fs) {
      var legend = fs.querySelector('legend');
      var slot = slotFor(legend ? legend.textContent : '');
      if (!slot) return;
      var checked = fs.querySelector('input[type="radio"]:checked');
      if (checked) pref[slot] = checked.value;
    });
    vs.querySelectorAll('select.select__select').forEach(function (sel) {
      var m = /options\[(.+)\]/.exec(sel.name || '');
      var slot = slotFor(m ? m[1] : '');
      if (slot && sel.value) pref[slot] = sel.value;
    });
    return Object.keys(pref).length ? pref : null;
  }

  function persistCurrent() {
    var pref = currentPrefFromDom();
    if (!pref) return;
    // Merge, don't replace: a product with no Height option must not wipe a
    // height the shopper picked two products ago.
    var merged = load() || {};
    ['size', 'dimension', 'height'].forEach(function (s) {
      if (pref[s]) merged[s] = pref[s];
    });
    merged.ts = Date.now();
    save(merged);
  }

  // Delegated so it survives product-info.js replacing the picker markup.
  var userTouched = false;
  document.addEventListener('change', function (e) {
    if (!e.target || !e.target.closest) return;
    if (!e.target.closest('variant-selects')) return;
    if (e.isTrusted) userTouched = true;          // a real shopper choice always wins
    else if (applying) return;                    // our own programmatic change
    setTimeout(persistCurrent, 0);
  }, true);

  /* -- resolve the best available variant for a saved preference --
   * Tiers, best first. Every slot listed must match exactly.
   */
  var TIERS = [
    ['size', 'dimension', 'height'],
    ['dimension', 'height'],
    ['size', 'dimension'],
    ['dimension'],
    ['size', 'height'],
    ['size']
  ];

  function resolve(pref) {
    var avail = data.variants.filter(function (v) { return v.a; });
    if (!avail.length) return null;

    for (var t = 0; t < TIERS.length; t++) {
      var want = TIERS[t];
      var usable = want.every(function (s) { return pref[s] && slotPos[s] !== undefined; });
      if (!usable) continue; // this product has no such option, or nothing saved for it
      var hit = avail.filter(function (v) {
        return want.every(function (s) { return norm(variantSlot(v, s)) === norm(pref[s]); });
      })[0];
      if (hit) return { variant: hit, tier: want.join('+') };
    }
    // Nothing matched - fall back to the first available ("first active") size.
    return { variant: avail[0], tier: 'first-available' };
  }

  /* -- apply a variant to the picker --
   * All options of the target variant are checked together and one change is
   * fired (see checkAll), then we wait for the render to settle and verify,
   * retrying a few times if the theme moved anything.
   */
  var STEP_TIMEOUT = 4000;

  function fieldsetFor(name) {
    var vs = document.querySelector('variant-selects');
    if (!vs) return null;
    var fieldsets = vs.querySelectorAll('fieldset.product-form__input');
    for (var f = 0; f < fieldsets.length; f++) {
      var legend = fieldsets[f].querySelector('legend');
      var lname = legend ? legend.textContent.trim().split('\n')[0].replace(/:$/, '').trim() : '';
      if (norm(lname) === norm(name)) return fieldsets[f];
    }
    return null;
  }

  function selectFor(name) {
    var vs = document.querySelector('variant-selects');
    if (!vs) return null;
    var sels = vs.querySelectorAll('select.select__select');
    for (var s = 0; s < sels.length; s++) {
      var m = /options\[(.+)\]/.exec(sels[s].name || '');
      if (m && norm(m[1]) === norm(name)) return sels[s];
    }
    return null;
  }

  function currentValue(name) {
    var fs = fieldsetFor(name);
    if (fs) {
      var c = fs.querySelector('input[type="radio"]:checked');
      return c ? c.value : null;
    }
    var sel = selectFor(name);
    return sel ? sel.value : null;
  }

  function wantFor(v, name) {
    return v.o[(data.options || []).indexOf(name)];
  }

  function matchesAll(v) {
    return (data.options || []).every(function (name) {
      var want = wantFor(v, name);
      if (want == null || want === '') return true;
      return norm(currentValue(name)) === norm(want);
    });
  }

  /* Check every option of the target variant at once and fire ONE change.
   *
   * Why all at once: the request the theme builds is the full set of checked
   * option values, so this asks for the exact target variant. Doing it one
   * option at a time briefly requests combinations that do not exist (Queen
   * with a Single-only dimension), which flashes "Unavailable" in the CTA.
   *
   * Why `disabled` is cleared: the radios for the target dimension/height are
   * rendered disabled under the current size. viewTransition keeps the old
   * picker in the DOM (hidden) for 500ms, and product-info.js re-clicks the
   * first enabled input of any non-size fieldset whose checked input is
   * disabled - on the old copy too. An enabled checked input is left alone.
   */
  function checkAll(v) {
    var fire = null;
    (data.options || []).forEach(function (name) {
      var want = wantFor(v, name);
      if (want == null || want === '') return;

      var fs = fieldsetFor(name);
      if (fs) {
        var inputs = fs.querySelectorAll('input[type="radio"]');
        for (var k = 0; k < inputs.length; k++) {
          if (norm(inputs[k].value) !== norm(want)) continue;
          inputs[k].removeAttribute('disabled');
          inputs[k].classList.remove('disabled');
          if (!inputs[k].checked) { inputs[k].checked = true; fire = inputs[k]; }
          break;
        }
        return;
      }

      var sel = selectFor(name);
      if (!sel) return;
      for (var oi = 0; oi < sel.options.length; oi++) {
        if (norm(sel.options[oi].value) !== norm(want)) continue;
        if (sel.selectedIndex !== oi) {
          sel.selectedIndex = oi;
          Array.prototype.forEach.call(sel.options, function (o) { o.removeAttribute('selected'); });
          sel.options[oi].setAttribute('selected', 'selected');
          fire = sel;
        }
        break;
      }
    });
    return fire;
  }

  /* What .custom_confirm_size does when the shopper closes the size sheet:
   * copy the variant into the visible size label and the sticky-bar select
   * (theme.liquid customselectOption). Driving the radios bypasses that
   * handler, so the label would otherwise keep the page-load variant. */
  function syncSizeLabel(variantId) {
    var id = String(variantId);
    ['custom_variant_select', 'custom_mobile_selected_variant'].forEach(function (elId) {
      document.querySelectorAll('[id="' + elId + '"]').forEach(function (sel) {
        for (var i = 0; i < sel.options.length; i++) {
          if (sel.options[i].value === id) { sel.selectedIndex = i; return; }
        }
      });
    });
  }

  /* The render has landed when product-info.js writes the target variant into
   * the cart form's hidden id (updateVariantInputs). That is the fastest
   * reliable signal - a mutation-quiet wait never fires on pages whose sliders
   * and sticky bar keep animating. */
  function cartVariantId() {
    var input = document.querySelector('form[action*="/cart/add"] input[name="id"]');
    return input ? String(input.value) : '';
  }
  function landed(v) {
    return cartVariantId() === String(v.id) && matchesAll(v);
  }
  function waitLanded(v, done) {
    var started = Date.now();
    (function poll() {
      if (landed(v)) return done(true);
      if (Date.now() - started > STEP_TIMEOUT) return done(false);
      setTimeout(poll, 60);
    })();
  }

  function applyVariant(v, done) {
    applying = true;
    var attempts = 0;
    var didAnything = false;
    var t0 = Date.now();

    function finish() {
      var ok = landed(v);
      if (ok) syncSizeLabel(v.id);
      // The theme's auto-snap can still fire late off the old picker copy.
      // Re-check once after it has had its chance and put things right.
      setTimeout(function () {
        if (userTouched) { applying = false; return; }
        if (landed(v)) { syncSizeLabel(v.id); applying = false; return; }
        var fire = attempts < 4 ? checkAll(v) : null;
        if (!fire) { applying = false; return; }
        fire.dispatchEvent(new Event('change', { bubbles: true }));
        waitLanded(v, function (ok) { if (ok) syncSizeLabel(v.id); applying = false; });
      }, 1200);
      done(didAnything && ok);
    }

    function tick() {
      if (landed(v)) return finish();
      if (attempts++ >= 4) return finish();

      var fire = checkAll(v);
      if (!fire) {
        // Mid-render, or the value simply isn't offered here.
        if (Date.now() - t0 > STEP_TIMEOUT * 3) return finish();
        return setTimeout(tick, 300);
      }
      didAnything = true;
      fire.dispatchEvent(new Event('change', { bubbles: true }));
      waitLanded(v, tick);
    }

    tick();
  }

  /* -- run -- */
  function run() {
    // Respect an explicit variant deep-link - the shopper asked for that one.
    if (/[?&]variant=/.test(window.location.search)) { persistCurrent(); return; }

    var pref = load();
    if (!pref || (!pref.size && !pref.dimension && !pref.height)) {
      persistCurrent(); // nothing stored yet: seed from this PDP
      return;
    }

    var picked = resolve(pref);
    if (!picked) return;
    if (picked.variant.id === data.selected) return; // already right - no flicker

    applyVariant(picked.variant, function (ok) {
      persistCurrent(); // record where we actually landed
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'dfx_size_memory_applied',
          dfx_size_match: picked.tier,
          dfx_size_variant: picked.variant.id,
          dfx_size_applied: !!ok
        });
      } catch (e) {}
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
